/**
 * Centralized API client — client-side only (uses localStorage for tokens).
 *
 * Features:
 *   - Attaches Authorization: Bearer <accessToken> to every request
 *   - On 401: attempts token refresh → retries original request
 *   - Queues concurrent requests during an in-flight refresh (no duplicate calls)
 *   - On refresh failure: clears auth state and redirects to /login
 *   - Typed error class (ApiError) with status code + server error code
 */

import { API } from '@/config/env'
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearAuth,
} from '@/utils/storage'
import type { TokenRefreshData } from '@/shared/types'

// ─────────────────────────────────────────────────────────────────────────────
// ERROR
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: { path: string; message: string }[]
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REFRESH QUEUE
// Ensures only one refresh call is in-flight at a time.
// Other 401s that arrive mid-refresh are queued and resolved when refresh done.
// ─────────────────────────────────────────────────────────────────────────────

let isRefreshing = false
let refreshQueue: Array<(accessToken: string) => void> = []

async function doRefresh(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new Error('No refresh token')

  const res = await fetch(API.REFRESH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })

  if (!res.ok) throw new Error('Refresh failed')

  const json = (await res.json()) as { success: boolean; data: TokenRefreshData }
  if (!json.success) throw new Error('Refresh failed')

  const { accessToken, refreshToken: newRefreshToken } = json.data
  setTokens(accessToken, newRefreshToken)
  return accessToken
}

function redirectToLogin(): void {
  clearAuth()
  if (typeof window !== 'undefined') {
    window.location.href = '/login'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CORE FETCH
// ─────────────────────────────────────────────────────────────────────────────

async function request<T>(
  url: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const accessToken = getAccessToken()

  const headers = new Headers(options.headers)
  if (options.body !== undefined && options.body !== null && !(options.body instanceof FormData)) {
    headers.set('Content-Type', headers.get('Content-Type') ?? 'application/json')
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const res = await fetch(url, { ...options, headers })

  // ── 401 handling ──────────────────────────────────────────────────────────
  // Only attempt refresh if there is actually a refresh token stored.
  // Without this guard, unauthenticated endpoints (login, signup) that return
  // 401 for bad credentials would trigger a redirect to /login.
  if (res.status === 401 && retry && getRefreshToken()) {
    if (isRefreshing) {
      // Another refresh is in progress — queue this retry
      return new Promise<T>((resolve, reject) => {
        refreshQueue.push((newToken) => {
          headers.set('Authorization', `Bearer ${newToken}`)
          request<T>(url, { ...options, headers }, false).then(resolve).catch(reject)
        })
      })
    }

    isRefreshing = true
    try {
      const newToken = await doRefresh()
      // Drain the queue
      refreshQueue.forEach((cb) => cb(newToken))
      refreshQueue = []
      isRefreshing = false
      // Retry the original request
      return request<T>(url, options, false)
    } catch {
      refreshQueue = []
      isRefreshing = false
      redirectToLogin()
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expired. Please log in again.')
    }
  }

  // ── Parse response ────────────────────────────────────────────────────────
  let body: any
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    body = await res.json()
  }

  if (!res.ok) {
    const message = body?.error ?? `HTTP ${res.status}`
    const code    = body?.code  ?? 'UNKNOWN_ERROR'
    throw new ApiError(res.status, code, message, body?.details)
  }

  return body as T
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC METHODS
// ─────────────────────────────────────────────────────────────────────────────

export const api = {
  get<T>(url: string, params?: Record<string, string | number | undefined>): Promise<T> {
    const fullUrl = params
      ? `${url}?${new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        )}`
      : url
    return request<T>(fullUrl, { method: 'GET' })
  },

  post<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },

  patch<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },

  delete<T>(url: string): Promise<T> {
    return request<T>(url, { method: 'DELETE' })
  },

  /** Multipart upload — does NOT set Content-Type (browser sets boundary automatically) */
  upload<T>(url: string, formData: FormData): Promise<T> {
    const headers = new Headers()
    const accessToken = getAccessToken()
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    return request<T>(url, { method: 'POST', headers, body: formData }, true)
  },

  /** DELETE with a JSON body (e.g. DELETE /api/users/me requires confirmation payload) */
  deleteWithBody<T>(url: string, body: unknown): Promise<T> {
    return request<T>(url, {
      method: 'DELETE',
      body: JSON.stringify(body),
    })
  },
}
